/**
 * AI Referrer Tracking for Agentic Commerce
 * Detects and tracks traffic from AI agents (ChatGPT, Perplexity, Claude, etc.)
 * @version 1.0.0
 * @marker pm_ai_referrer_tracking_enabled
 */

import {
  isAnalyticsAllowed,
  isAnalyticsConsentGranted,
} from '@/lib/consent-manager'
import { matchAiHost, type AISource } from '@/lib/analytics/ai-sources'

// Re-exported so existing importers keep working. The definitions live in
// ai-sources.ts, which the ingest route and the GA4 client share.
export {
  AI_HOSTS,
  AI_HOSTNAMES,
  AI_SOURCE_LABELS,
  ALLOWED_AI_SOURCES,
  getAISourceName,
  matchAiHost,
} from '@/lib/analytics/ai-sources'
export type { AISource } from '@/lib/analytics/ai-sources'

/**
 * The ONE place that knows this key exists and which storage backs it.
 *
 * It used to live in sessionStorage, and `useVectorChat` hand-read it with a
 * literal `sessionStorage.getItem('pm_ai_referrer')`. When the medium moved to
 * localStorage the chat reader kept pointing at the empty one and attribution
 * silently died. Nothing outside this module reads the key any more, so a
 * storage change cannot desynchronise a caller again.
 */
const STORAGE_KEY = 'pm_ai_referrer'

/**
 * Consent check that can never throw.
 *
 * Mirrors visitor-id.ts. Failing closed is the safe direction: worst case an
 * attribution read is skipped, never a thrown error inside a contact submit or
 * a chat request.
 */
function consentAllowed(): boolean {
  try {
    return isAnalyticsAllowed() === true
  } catch {
    return false
  }
}

interface AIReferrerData {
  source: AISource
  /** Empty for a tagged landing: no referrer was observed. */
  referrer: string
  isAIReferred: boolean
  timestamp: string
}

/**
 * `utm_source` values an AI platform documents adding to its own outbound
 * links. Only documented tags belong here, never a guess: OpenAI documents
 * `utm_source=chatgpt.com` on ChatGPT referral links (help.openai.com,
 * "Publishers and developers FAQ").
 *
 * A Map, not an object literal: an object lookup would return inherited
 * members for tags like `constructor` or `__proto__`, and a truthy built-in
 * would be stored as an AI first touch.
 */
const DOCUMENTED_AI_UTM_SOURCES: ReadonlyMap<string, AISource> = new Map([
  ['chatgpt.com', 'chatgpt'],
])

/**
 * The documented AI tag on THIS landing's URL, if any. Reads the current
 * location, not the stored first-touch UTM, so an older campaign tag can
 * never be mistaken for today's AI landing.
 */
function detectTaggedAISource(): AISource | null {
  try {
    const tag = new URLSearchParams(window.location.search)
      .get('utm_source')
      ?.trim()
      .toLowerCase()
    return tag ? (DOCUMENTED_AI_UTM_SOURCES.get(tag) ?? null) : null
  } catch {
    return null
  }
}

/**
 * Detect whether this landing was referred by an LLM.
 *
 * UNDERCOUNTS BY DESIGN. Many genuine LLM click-throughs arrive with NO
 * referrer — native mobile apps, copied links, and privacy modes strip it —
 * and are indistinguishable from direct traffic. The one exception is a
 * landing carrying a platform's own documented tag (DOCUMENTED_AI_UTM_SOURCES):
 * with no referrer at all, that tag is counted as a TAGGED referral. It is an
 * attribution signal, not proof of origin: it is sent with an empty referrer
 * and stored as NULL, which admin shows as "no referrer" rather than as
 * verified tagged traffic. An observed referrer always wins over a tag.
 *
 * Deliberately NO heuristic fallback. The previous version treated any
 * referrer containing '/chat', '/ai', '/assistant' or '/search' as AI, which
 * matched ordinary search engines and unrelated hosts. An unrecognised
 * referrer is now simply not AI-referred: a missed referral is a far cheaper
 * error than a search visit misreported as an LLM referral.
 */
export function detectAIReferrer(): AIReferrerData {
  const timestamp = new Date().toISOString()

  if (typeof window === 'undefined') {
    return { source: 'unknown', referrer: '', isAIReferred: false, timestamp }
  }

  const referrer = document.referrer || ''
  if (!referrer) {
    const tagged = detectTaggedAISource()
    if (tagged) {
      return { source: tagged, referrer: '', isAIReferred: true, timestamp }
    }
    return { source: 'unknown', referrer: '', isAIReferred: false, timestamp }
  }

  let hostname: string
  try {
    hostname = new URL(referrer).hostname
  } catch {
    // Malformed referrer — not AI-referred, never throw.
    return { source: 'unknown', referrer, isAIReferred: false, timestamp }
  }

  const source = matchAiHost(hostname)
  if (source) return { source, referrer, isAIReferred: true, timestamp }

  return { source: 'unknown', referrer, isAIReferred: false, timestamp }
}

/**
 * Store AI referrer data in localStorage (survives sessions, so a returning
 * visitor keeps their first-touch source).
 *
 * Consent-gated on write AND read. In sessionStorage an ungated record died
 * with the tab; in localStorage it would outlive a consent withdrawal and keep
 * being attached to contact submissions and chat requests indefinitely.
 */
export function storeAIReferrer(data: AIReferrerData): void {
  if (typeof window === 'undefined') return
  if (!consentAllowed()) return

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    console.error('Failed to store AI referrer:', error)
  }
}

/**
 * Retrieve stored AI referrer data.
 *
 * Gating the READ (not just the write) is what makes this safe: consent can be
 * withdrawn, or silently expire, after a record was legitimately written. In
 * that case the stored value is cleared rather than returned, so no caller can
 * forward stale attribution regardless of when it was captured.
 */
export function getStoredAIReferrer(): AIReferrerData | null {
  if (typeof window === 'undefined') return null
  if (!consentAllowed()) {
    clearStoredAIReferrer()
    return null
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const data = JSON.parse(stored) as AIReferrerData
    return data.isAIReferred ? data : null
  } catch (error) {
    console.error('Failed to retrieve AI referrer:', error)
    return null
  }
}

/**
 * The stored source alone, for callers that only need the label (chat).
 * Exists so no caller has to know the key or the storage medium.
 */
export function getAIReferrerSource(): AISource | undefined {
  return getStoredAIReferrer()?.source
}

/** Called on consent withdrawal, alongside clearFirstTouchUtm/clearVisitorId. */
export function clearStoredAIReferrer(): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // no-op
  }
}

/**
 * Track AI referrer with GA4
 */
export function trackAIReferrer(data: AIReferrerData): void {
  if (typeof window === 'undefined' || !data.isAIReferred) return

  // window.gtag exists for everyone (consent-default stub), so a bare
  // gtag check is NOT a consent check — guard here so any future direct
  // caller can't bypass the consent gate at AIReferrerTracker.tsx
  if (!isAnalyticsConsentGranted()) return

  // Send to GA4 if available
  if (window.gtag) {
    window.gtag('event', 'ai_referral_detected', {
      ai_source: data.source,
      ai_referrer: data.referrer,
      event_category: 'agentic_commerce',
      event_label: `AI Source: ${data.source}`,
    })
  }

  // Note: Microsoft Clarity custom dimensions would require additional setup
  // For now, we rely on GA4 events for AI source tracking
}

/**
 * Initialize AI referrer tracking (call on page load)
 */
export function initAIReferrerTracking(): AIReferrerData | null {
  if (typeof window === 'undefined') return null

  // The landing beacon must reflect this page load, not a historical
  // first-touch record kept for contact/chat attribution.
  const detected = detectAIReferrer()
  if (!detected.isAIReferred) return null

  // Preserve the first AI touch, but still track a later AI landing using its
  // current source so last-touch reporting can move independently.
  if (!getStoredAIReferrer()) {
    storeAIReferrer(detected)
  }

  trackAIReferrer(detected)
  return detected
}
